import { QuestionForMain } from "@/components/features/_layouts/island/main/question";
import type { SiteControllerSettingTllFormModel } from "@/components/features/settings/site-controller/model/tl-lincoln/type";
import type { SiteControllerSettingTLLincolnFormModelMethodsType } from "@/components/features/settings/site-controller/model/tl-lincoln/use-case/use-create-model/methods";

import { SelectField } from "@/components/ui/form/fields/select-field";

import { useTlLincolnPlanMapping } from "./hooks";

type Props = {
	rooms: SiteControllerSettingTllFormModel["ViewModel"]["tll"]["rooms"]; //
	plans: SiteControllerSettingTllFormModel["ViewModel"]["tll"]["plans"]; //
	defaultPlan: SiteControllerSettingTllFormModel["ViewModel"]["tll"]["defaultPlan"]; //

	onSetDefaultPlan: SiteControllerSettingTLLincolnFormModelMethodsType["siteControllerSettingTLLFormModelMethods"]["onSetDefaultPlan"];
};

const SectionWrapper = ({ children, title }: { children: React.ReactNode; title: string }) => {
	return (
		<div className={"space-y-2"}>
			<p className="text-sm font-semibold underline underline-offset-4">{title}</p>
			<div className="ml-3">{children}</div>
		</div>
	);
};

const KidsPolicySection = ({
	title,
	acceptingPolicy,
	capacityPolicy,
}: {
	title: string;
	acceptingPolicy: boolean;
	capacityPolicy: boolean;
}) => {
	return (
		<div className="flex items-center gap-x-4">
			<p className="w-32">{title}</p>
			<p>：</p>

			<div className="flex items-center gap-x-2">
				<p className="">
					「宿泊を
					{acceptingPolicy ? "受け入れる" : "受け入れない"}」
				</p>
				<p>,</p>
				<p className="">
					「定員に
					{capacityPolicy ? "含める" : "含めない"}」
				</p>
			</div>
		</div>
	);
};

export const SettingsSiteControllerTLLPlanMapping = ({ rooms, plans, defaultPlan, onSetDefaultPlan }: Props) => {
	const { defaultPlanOptions, selectedPlanInfo, availableRoomsText } = useTlLincolnPlanMapping({
		rooms,
		plans, //
		defaultPlan,
	});

	return (
		<>
			{Boolean(plans.value.length) && (
				<QuestionForMain
					required={true}
					title={"CHILLNNの基本料金となる、TL-リンカーンのプランの選択"}
					inputChildren={
						<div className="space-y-6">
							<div className="w-2/3">
								<SelectField
									value={defaultPlan?.value}
									options={defaultPlanOptions}
									handleValueChange={onSetDefaultPlan}
								/>
							</div>

							{selectedPlanInfo && (
								<div className="space-y-4">
									<SectionWrapper title="利用可能な部屋">
										<p>{availableRoomsText}</p>
									</SectionWrapper>

									<SectionWrapper title="子供の受け入れ設定">
										<KidsPolicySection
											title={"未就学児(0~3歳)"}
											acceptingPolicy={selectedPlanInfo.acceptInfantWithoutFoodAndBed.value}
											capacityPolicy={selectedPlanInfo.countableInfantWithoutFoodAndBed.value}
										/>
										<KidsPolicySection
											title={"未就学児(4~6歳)"}
											acceptingPolicy={selectedPlanInfo.acceptInfant.value}
											capacityPolicy={selectedPlanInfo.countableInfant.value}
										/>
										<KidsPolicySection
											title={"小学校低学年"}
											acceptingPolicy={selectedPlanInfo.acceptPrimarySchoolLowerGrade.value}
											capacityPolicy={selectedPlanInfo.countablePrimarySchoolLowerGrade.value}
										/>
										<KidsPolicySection
											title={"小学校高学年"}
											acceptingPolicy={selectedPlanInfo.acceptPrimarySchoolUpperGrade.value}
											capacityPolicy={selectedPlanInfo.countablePrimarySchoolUpperGrade.value}
										/>
									</SectionWrapper>
								</div>
							)}
						</div>
					}
				/>
			)}
		</>
	);
};

SettingsSiteControllerTLLPlanMapping.displayName = "SettingsSiteControllerTLLPlanMapping";
